// Time and schedule constants + formatting helpers used by UI components.

// Stable fallback to avoid re-creating `{}` on every render (which can trigger
// effects that depend on `connection?.windows` to loop while data is loading).
export const EMPTY_CONNECTION_WINDOWS: Record<string, unknown> = {}

export const DAY_KEYS: Array<{ key: string; label: string }> = [
  { key: 'monday', label: 'M' },
  { key: 'tuesday', label: 'T' },
  { key: 'wednesday', label: 'W' },
  { key: 'thursday', label: 'T' },
  { key: 'friday', label: 'F' },
  { key: 'saturday', label: 'S' },
  { key: 'sunday', label: 'S' },
]

export const US_TIMEZONE_OPTIONS = [
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
]

// Convert time to 12-hour display format (e.g., "19:00:00" -> "7:00 PM").
// NOTE: Returns a natural display string without a leading zero on the hour
// (e.g. "7:00 PM", not "07:00 PM"). This intentionally differs from
// formatTimeParts / parseTimeParts which use zero-padded hours for input fields.
export function formatTo12Hour(timeStr: string): string {
  if (!timeStr || typeof timeStr !== 'string') return ''

  // If already in 12-hour format with AM/PM, return as-is
  if (/\s*(AM|PM)\s*$/i.test(timeStr)) {
    return timeStr.trim()
  }

  // Parse 24-hour format like "10:00:00" or "19:00:00" or "10:00"
  const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (!match || !match[1] || !match[2]) {
    return timeStr // Return as-is if can't parse
  }

  let hour = parseInt(match[1], 10)
  const minute = match[2]
  const period = hour >= 12 ? 'PM' : 'AM'

  // Convert to 12-hour format
  if (hour === 0) {
    hour = 12
  } else if (hour > 12) {
    hour = hour - 12
  }

  return `${hour}:${minute} ${period}`
}

// Convert time to API 24-hour format with seconds (e.g., "10:00 AM" -> "10:00:00", "7:00 PM" -> "19:00:00")
export function formatTo24Hour(timeStr: string): string {
  if (!timeStr || typeof timeStr !== 'string') return ''

  const trimmed = timeStr.trim()
  if (!trimmed) return ''

  // Already in 24-hour "HH:MM:SS"
  const withSeconds = trimmed.match(/^(\d{1,2}):(\d{2}):(\d{2})$/)
  if (withSeconds && withSeconds[1] && withSeconds[2] && withSeconds[3]) {
    const hour = String(parseInt(withSeconds[1], 10)).padStart(2, '0')
    return `${hour}:${withSeconds[2]}:${withSeconds[3]}`
  }

  // 24-hour "HH:MM" (no seconds)
  const noSeconds = trimmed.match(/^(\d{1,2}):(\d{2})$/)
  if (noSeconds && noSeconds[1] && noSeconds[2]) {
    const hour = String(parseInt(noSeconds[1], 10)).padStart(2, '0')
    return `${hour}:${noSeconds[2]}:00`
  }

  // 12-hour "H:MM AM/PM"
  const twelveHour = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (twelveHour && twelveHour[1] && twelveHour[2] && twelveHour[3]) {
    let hour = parseInt(twelveHour[1], 10)
    const minute = twelveHour[2]
    const period = twelveHour[3].toUpperCase()

    if (period === 'AM') {
      if (hour === 12) hour = 0
    } else {
      if (hour !== 12) hour = hour + 12
    }

    const hourStr = String(hour).padStart(2, '0')
    return `${hourStr}:${minute}:00`
  }

  // Fallback: return as-is if unrecognized
  return trimmed
}

export type TimeParts = {
  hour: string
  minute: string
  period: 'AM' | 'PM'
}

// Parse time string into parts
export function parseTimeParts(timeStr: string): TimeParts {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (match && match[1] && match[2] && match[3]) {
    return {
      hour: String(parseInt(match[1], 10)).padStart(2, '0'),
      minute: match[2],
      period: match[3].toUpperCase() as 'AM' | 'PM',
    }
  }
  // Fallback to 8:00 AM when the input string is empty or malformed,
  // so TimeInput fields always have valid values to display.
  return { hour: '08', minute: '00', period: 'AM' }
}

// Format time parts back to string
export function formatTimeParts(
  hour: string,
  minute: string,
  period: 'AM' | 'PM'
): string {
  return `${hour}:${minute} ${period}`
}

// Turns a start and end time into a duration string in minutes and seconds
export function formatDuration(
  start: string | null,
  end: string | null
): string {
  if (!start || !end) {
    return '-'
  }
  const startDate = new Date(start)
  const endDate = new Date(end)
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return '-'
  }
  const diffMs = endDate.getTime() - startDate.getTime()
  if (diffMs < 0) {
    return '-'
  }
  const totalSeconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes === 0) {
    return `${seconds}s`
  }
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`
}
